import { useEffect, useRef, useState, type RefObject } from 'react';

/** Screen-px movement before a press counts as a pan rather than a click —
 *  below this, the press is left alone so a motionless click still selects
 *  an area / opens the drawer exactly as before drag-to-pan existed. */
const PAN_THRESHOLD_PX = 4;

interface IPanSession {
  pointerId: number;
  startX: number;
  startY: number;
  startScrollLeft: number;
  startScrollTop: number;
}

interface IUseStagePanArgs {
  /** The scrollable stage container (`MapStage`'s `stageOuterRef`) — pan
   *  reads/writes its own `scrollLeft`/`scrollTop` directly and the browser
   *  clamps them for free, and `pannable` is measured off its own
   *  `scrollWidth`/`scrollHeight` vs. `clientWidth`/`clientHeight`. */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Only view mode pans — a draw/place/set-radius tool owns the pointer
   *  instead (`MapStage`'s own `interactive` flag, inverted). */
  enabled: boolean;
  /** Zoom step (1 / 1.5 / 2) — `pannable` is true outright above 1×, even
   *  before a measured overflow lands (e.g. the very first paint). */
  zoom: number;
}

/**
 * Drag-to-pan for the site-map stage, view mode only. Pressing anywhere on
 * the stage — including over area polygons/chips and the blueprint itself —
 * scrolls the container by the pointer's own delta once the press clears a
 * small movement threshold; below that threshold nothing happens, so a
 * motionless click still reaches the polygon/chip's own `onClick` (area
 * selection) unchanged.
 *
 * Equipment chip and radius-handle drags start on their OWN elements and
 * call `stopPropagation` in `useEquipmentEditing`'s `startDrag` — that keeps
 * their `pointerdown` from ever reaching this hook's container-level
 * listener, so there is nothing to special-case for them here.
 *
 * Listeners live on the container itself (not `window`), matching
 * `setPointerCapture`'s own contract: once a pan starts, capture re-targets
 * subsequent `pointermove`/`pointerup` to the container regardless of where
 * the pointer physically is, so a fast drag past the stage's edge keeps
 * panning instead of dropping the gesture.
 */
export function useStagePan({ containerRef, enabled, zoom }: IUseStagePanArgs) {
  const [isPanning, setIsPanning] = useState(false);
  const [isPannable, setIsPannable] = useState(false);
  const sessionRef = useRef<IPanSession | null>(null);
  // Whether THIS session's movement has already cleared the threshold — read
  // by the capturing click guard below to swallow the click a real pan
  // leaves behind, and reset once that click has been handled (or once a
  // fresh pointerdown starts a new session).
  const pannedRef = useRef(false);

  // Overflow measurement — re-checked on zoom changes and on the container's
  // own size changes (sidebar collapse, floor switch, window resize).
  // Guarded for jsdom, which has no `ResizeObserver` (mirrors `MapStage`'s
  // own `boundWidth` effect) — falls back to the `zoom > 1` half of
  // `pannable` alone.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const overflowing = el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
      setIsPannable(zoom > 1 || overflowing);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [containerRef, zoom]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !enabled) return;

    const onPointerDown = (e: PointerEvent) => {
      // Only the primary button/touch/pen contact starts a pan — mirrors
      // the browser's own click semantics (a middle/right-click never
      // selects, so it shouldn't start a pan either).
      if (e.button !== 0) return;
      sessionRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startScrollLeft: el.scrollLeft,
        startScrollTop: el.scrollTop,
      };
      pannedRef.current = false;
    };

    const onPointerMove = (e: PointerEvent) => {
      const session = sessionRef.current;
      if (!session || e.pointerId !== session.pointerId) return;
      const dx = e.clientX - session.startX;
      const dy = e.clientY - session.startY;
      if (!pannedRef.current) {
        if (Math.abs(dx) < PAN_THRESHOLD_PX && Math.abs(dy) < PAN_THRESHOLD_PX) return;
        pannedRef.current = true;
        setIsPanning(true);
        // Captured lazily, on the first frame that actually pans (not on
        // pointerdown) — jsdom doesn't implement it at all, hence the guard.
        if (typeof el.setPointerCapture === 'function') el.setPointerCapture(session.pointerId);
      }
      el.scrollLeft = session.startScrollLeft - dx;
      el.scrollTop = session.startScrollTop - dy;
    };

    const endSession = (e: PointerEvent) => {
      const session = sessionRef.current;
      if (!session || e.pointerId !== session.pointerId) return;
      if (typeof el.hasPointerCapture === 'function' && el.hasPointerCapture(session.pointerId)) {
        el.releasePointerCapture(session.pointerId);
      }
      sessionRef.current = null;
      setIsPanning(false);
      // `pannedRef` itself is NOT cleared here — the capturing click guard
      // below reads it once more (the browser's own `click` fires right
      // after this `pointerup`) and clears it there, so a real pan's
      // trailing click never reaches the polygon/chip it started over.
    };

    // Capture phase, on the container: runs BEFORE the click ever reaches
    // the actual target (a polygon's or chip's own `onClick`), so a pan's
    // trailing click is stopped before it can select/drag anything.
    const onClickCapture = (e: MouseEvent) => {
      if (pannedRef.current) {
        e.stopPropagation();
        e.preventDefault();
      }
      pannedRef.current = false;
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endSession);
    el.addEventListener('pointercancel', endSession);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', endSession);
      el.removeEventListener('pointercancel', endSession);
      el.removeEventListener('click', onClickCapture, true);
      sessionRef.current = null;
      pannedRef.current = false;
      setIsPanning(false);
    };
  }, [containerRef, enabled]);

  return { isPanning, isPannable };
}
